import Kendra, { DocumentAttributeValueType } from 'aws-sdk/clients/kendra';
import _ from 'lodash';
import {
    attributeValueEquals,
    attributeValueListContains,
    attributeValueToDate,
    attributeValueToString,
    DocumentAttributeValueTypeEnum,
    getValueType,
    IndexFieldNameToDocumentAttributeValueType,
    isNullOrEmpty,
} from './utils';
import { DateRange, SelectedFacetRetriever } from './SelectedFacetRetriever';
import dayjs from 'dayjs';

export type DocumentAttributeStringSelections = {
    [attributeKey: string]: Kendra.DocumentAttributeValue[];
};

export type DocumentAttributeDateRangeSelections = {
    [attributeKey: string]: DateRange;
};

export class SelectedFacetManager implements SelectedFacetRetriever {
    private stringFilters;
    private dateFilters;
    constructor(
        stringFilters: DocumentAttributeStringSelections,
        dateFilters: DocumentAttributeDateRangeSelections
    ) {
        this.stringFilters = stringFilters;
        this.dateFilters = dateFilters;
    }

    static empty(): SelectedFacetManager {
        return new SelectedFacetManager({}, {});
    }

    getStringSelectionsOf(
        attributeKey: string
    ): Kendra.DocumentAttributeValue[] {
        return this.stringFilters[attributeKey];
    }

    getDateRangeSelectionsOf(attributeKey: string): DateRange | undefined {
        return this.dateFilters[attributeKey];
    }

    isSelected(
        attributeKey: string,
        attributeValue: Kendra.DocumentAttributeValue
    ): boolean {
        const attributeValueList: Kendra.DocumentAttributeValue[] =
            this.getStringSelectionsOf(attributeKey);
        return attributeValueListContains(attributeValueList, attributeValue);
    }

    getAllSelected(): {
        [key: string]: Kendra.DocumentAttributeValue[];
    } {
        const res: {
            [key: string]: Kendra.DocumentAttributeValue[];
        } = {};

        // get all string type selections
        Object.entries(this.stringFilters)
            .filter(
                ([_attributeKey, attributeValue]) => !_.isEmpty(attributeValue)
            )
            .forEach(([attributeKey, attributeValue]) => {
                res[attributeKey] = attributeValue;
            });

        // get all date type selections
        Object.entries(this.dateFilters)
            .filter(
                ([_attributeKey, dateRange]) =>
                    dateRange && dateRange.min && dateRange.max
            )
            .forEach(
                ([attributeKey, dateRange]) =>
                    (res[attributeKey] = [
                        { DateValue: dateRange.min.toDate() },
                        { DateValue: dateRange.max.toDate() },
                    ])
            );

        return res;
    }

    isEmpty(): boolean {
        return _.isEmpty(this.stringFilters) && _.isEmpty(this.dateFilters);
    }

    clearAll(): SelectedFacetManager {
        return new SelectedFacetManager({}, {});
    }

    private clear(
        attributeKey: string,
        attributeValue: Kendra.DocumentAttributeValue
    ): SelectedFacetManager {
        let attributeValueList = this.getStringSelectionsOf(attributeKey);

        if (
            !isNullOrEmpty(attributeValueList) &&
            attributeValueListContains(attributeValueList, attributeValue)
        ) {
            attributeValueList = attributeValueList.filter(
                (item) => !attributeValueEquals(item, attributeValue)
            );
        }
        return new SelectedFacetManager(
            { ...this.stringFilters, [attributeKey]: attributeValueList },
            this.dateFilters
        );
    }

    private set(
        attributeKey: string,
        attributeValue: Kendra.DocumentAttributeValue
    ): SelectedFacetManager {
        const attributeValueList: Kendra.DocumentAttributeValue[] =
            this.getStringSelectionsOf(attributeKey) || [];
        if (!attributeValueListContains(attributeValueList, attributeValue)) {
            const newAttributeValueList = [
                ...attributeValueList,
                attributeValue,
            ];
            return new SelectedFacetManager(
                {
                    ...this.stringFilters,
                    [attributeKey]: newAttributeValueList,
                },
                this.dateFilters
            );
        }
        return new SelectedFacetManager(this.stringFilters, this.dateFilters);
    }

    setIsSelected(
        attributeKey: string,
        attributeValue: Kendra.DocumentAttributeValue,
        isSelected: boolean
    ): SelectedFacetManager {
        if (isSelected) {
            return this.set(attributeKey, attributeValue);
        } else {
            return this.clear(attributeKey, attributeValue);
        }
    }

    clearSelectionsOf(attributeKey: string): SelectedFacetManager {
        delete this.stringFilters[attributeKey];
        return new SelectedFacetManager(this.stringFilters, this.dateFilters);
    }

    clearDateSelectionsOf(attributeKey: string): SelectedFacetManager {
        delete this.dateFilters[attributeKey];
        return new SelectedFacetManager(this.stringFilters, this.dateFilters);
    }

    setDateRange(
        attributeKey: string,
        value?: [number, number]
    ): SelectedFacetManager {
        if (value) {
            const minValue = dayjs(new Date(value[0], 0))
                .utc()
                .endOf('year')
                .millisecond(0);

            const maxValue = dayjs(new Date(value[1], 0))
                .utc()
                .endOf('year')
                .millisecond(0);

            return new SelectedFacetManager(this.stringFilters, {
                ...this.dateFilters,
                [attributeKey]: { min: minValue, max: maxValue },
            });
        } else {
            return this.clearDateSelectionsOf(attributeKey);
        }
    }

    selectDateAttributeFilter = (
        attributeKey: string,
        attributeList: Kendra.DocumentAttributeValue[]
    ): Kendra.AttributeFilter[] => {
        // If facet was returned as StringValue, convert to DateValue
        const dates: Date[] = [];
        for (const attributeValue of attributeList) {
            const date = attributeValueToDate(attributeValue);
            if (date) {
                dates.push(date.toDate());
            }
        }

        if (_.isEmpty(dates)) {
            return [];
        }

        // Compute max range
        const range: { min: Date; max: Date } = {
            max: dates[0],
            min: dates[0],
        };

        for (const date of dates) {
            const dateValueMs = date.valueOf();
            if (dateValueMs < range.min.valueOf()) {
                range.min = date;
            }

            if (dateValueMs > range.max.valueOf()) {
                range.max = date;
            }
        }

        return [
            {
                AndAllFilters: [
                    {
                        GreaterThanOrEquals: {
                            Key: attributeKey,
                            Value: { DateValue: range.min },
                        },
                    },
                    {
                        LessThanOrEquals: {
                            Key: attributeKey,
                            Value: { DateValue: range.max },
                        },
                    },
                ],
            },
        ];
    };

    selectStringListAttributeFilter = (
        attributeKey: string,
        attributeList: Kendra.DocumentAttributeValue[]
    ): Kendra.AttributeFilter[] => {
        const stringValueList: string[] = [];
        for (const attributeValue of attributeList) {
            if (attributeValue && attributeValue.StringValue) {
                stringValueList.push(attributeValueToString(attributeValue));
            }
        }

        return [
            {
                ContainsAny: {
                    Key: attributeKey,
                    Value: { StringListValue: stringValueList },
                },
            },
        ];
    };

    selectLongAttributeFilter = (
        attributeKey: string,
        attributeList: Kendra.DocumentAttributeValue[]
    ): Kendra.AttributeFilter[] => {
        const attributeFilterList: Kendra.AttributeFilterList = [];

        for (const attributeValue of attributeList) {
            if (attributeValue.LongValue) {
                attributeFilterList.push({
                    EqualsTo: {
                        Key: attributeKey,
                        Value: { LongValue: attributeValue.LongValue },
                    },
                });
            } else if (attributeValue.StringValue) {
                attributeFilterList.push({
                    EqualsTo: {
                        Key: attributeKey,
                        Value: { StringValue: attributeValue.StringValue },
                    },
                });
            }
        }

        return [
            {
                OrAllFilters: attributeFilterList,
            },
        ];
    };

    selectAttributeFilterForAttributeList = (
        attributeKey: string,
        attributeList: Kendra.DocumentAttributeValue[],
        attributeType: DocumentAttributeValueType
    ): Kendra.AttributeFilter[] => {
        if (_.isEmpty(attributeList)) {
            return [];
        }

        // Handle special cases based on the *index field type* (NOT the "seen" type, may need to convert)
        if (attributeType === DocumentAttributeValueTypeEnum.DateValue) {
            return this.selectDateAttributeFilter(attributeKey, attributeList);
        } else if (
            attributeType === DocumentAttributeValueTypeEnum.StringListValue
        ) {
            return this.selectStringListAttributeFilter(
                attributeKey,
                attributeList
            );
        } else if (attributeType === DocumentAttributeValueTypeEnum.LongValue) {
            return this.selectLongAttributeFilter(attributeKey, attributeList);
        } else {
            const attributeFilterList: Kendra.AttributeFilterList = [];

            for (const attributeValue of attributeList) {
                attributeFilterList.push({
                    EqualsTo: {
                        Key: attributeKey,
                        Value: attributeValue,
                    },
                });
            }

            return [
                {
                    OrAllFilters: attributeFilterList,
                },
            ];
        }
    };

    buildAttributeFilter(
        attributeTypeLookup?: IndexFieldNameToDocumentAttributeValueType
    ): Kendra.AttributeFilter | undefined {
        const attributeList = this.getAllSelected();

        if (!attributeTypeLookup) {
            return undefined;
        }

        let filters: Kendra.AttributeFilter[] = [];

        for (const attributeKey in attributeList) {
            if (attributeList.hasOwnProperty(attributeKey)) {
                const attributeValueList = attributeList[attributeKey];

                if (!_.isEmpty(attributeValueList)) {
                    // Get index field type, or fall back to "seen" type
                    const attribType =
                        attributeTypeLookup[attributeKey] ||
                        getValueType(attributeValueList[0]);
                    const filter = this.selectAttributeFilterForAttributeList(
                        attributeKey,
                        attributeValueList,
                        attribType
                    );

                    if (!isNullOrEmpty(filter)) {
                        filters = filters.concat(filter);
                    }
                }
            }
        }

        if (_.isEmpty(filters)) {
            return undefined;
        }

        return {
            AndAllFilters: filters,
        };
    }
}
